import { useEffect, useState, useRef, useCallback } from "react";
import {
  addDoc, collection, onSnapshot, orderBy, query,
  serverTimestamp, doc, updateDoc, setDoc, getDoc, deleteDoc
} from "firebase/firestore";
import { Clock, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import { db } from "../firebase-config";
import { REVEAL_STAGES, hashString, buildFactCandidates, renderFact } from "../opponentIntel";
import { filterProfanity } from "../profanityFilter";
import { getEquippedItem } from "../cosmetics";
import { BUBBLE_RADIUS } from "./ShopItemPreview";
import PayoffMatrixGrid from "./PayoffMatrixGrid";
import MoveIcon from "./MoveIcon";
import AvatarRing from "./AvatarRing";
import SubtitleBadge from "./SubtitleBadge";
import Logo from "./Logo";
import { TRUST_COLOR, TRUST_BG, BETRAY_COLOR, BETRAY_BG, MIXED_COLOR, TEXT_DARK, TEXT_MUTED, BORDER, GRADIENT_BG } from "../theme";

const GAME_DURATION = 180000;
const DECISION_WINDOW = 10000;

function Chat({ user, userProfile, gameId, shopItems = [] }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [gameData, setGameData] = useState(null);
  const [opponentProfile, setOpponentProfile] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(GAME_DURATION);
  const [showIntro, setShowIntro] = useState(true);
  const [showRules, setShowRules] = useState(false);
  const introCheckedRef = useRef(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const postedStagesRef = useRef(new Set());

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    return onSnapshot(doc(db, "games", gameId), (snapshot) => {
      setGameData(snapshot.data());
    });
  }, [gameId]);

  useEffect(() => {
    if (!gameData) return;
    const opponentId = gameData.playerA === user.uid ? gameData.playerB : gameData.playerA;

    getDoc(doc(db, "users", opponentId)).then((snapshot) => {
      if (snapshot.exists()) {
        setOpponentProfile(snapshot.data());
      }
    });
  }, [gameData, user.uid]);

  useEffect(() => {
    const q = query(collection(db, "games", gameId, "messages"), orderBy("clientCreatedAt"));
    return onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [gameId]);

  useEffect(() => {
    if (!gameData || !gameData.createdAt) return;

    const interval = setInterval(() => {
      const startTime = gameData.createdAt.toMillis();
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, GAME_DURATION - elapsed);

      setTimeRemaining(remaining);

      if (remaining === 0) {
        const isPlayerA = gameData.playerA === user.uid;
        const myMove = isPlayerA ? gameData.playerAMove : gameData.playerBMove;

        if (!myMove) {
          makeMove("cooperate");
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, [gameData, user.uid]);

  // Clear our typing flag if we leave with text still pending
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  // Only play the match-start intro if we just entered a fresh game
  // (avoids replaying it over an already-in-progress chat on reload).
  useEffect(() => {
    if (!gameData || !gameData.createdAt || !opponentProfile || introCheckedRef.current) return;
    introCheckedRef.current = true;
    const age = Date.now() - gameData.createdAt.toMillis();
    setShowIntro(age < 8000);
  }, [gameData, opponentProfile]);

  // At fixed remaining-time marks, reveal a true fact about each of the two
  // players into the shared chat, at the same time. Both clients independently
  // cross the same threshold and try to post — a deterministic per-stage doc ID
  // plus the messages rule only allowing "create" (never update) means whichever
  // client gets there first wins and the second's write is silently denied.
  useEffect(() => {
    if (!gameData || !opponentProfile) return;
    REVEAL_STAGES.forEach((stage) => {
      if (timeRemaining <= stage.thresholdMs && !postedStagesRef.current.has(stage.id)) {
        postedStagesRef.current.add(stage.id);
        postReveal(stage.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRemaining, gameData, opponentProfile]);

  const myTypingField = gameData && gameData.playerA === user.uid ? "typingA" : "typingB";

  const handleTextChange = (e) => {
    const value = e.target.value;
    setText(value);
    if (!gameData) return;

    updateDoc(doc(db, "games", gameId), { [myTypingField]: true }).catch(() => {});
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      updateDoc(doc(db, "games", gameId), { [myTypingField]: false }).catch(() => {});
    }, 2000);
  };

  const sendMessage = async () => {
    if (!text.trim() || !gameData) return;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    await addDoc(collection(db, "games", gameId, "messages"), {
      text,
      sender: user.displayName,
      senderUid: user.uid,
      createdAt: serverTimestamp(),
      clientCreatedAt: Date.now(),
    });
    await updateDoc(doc(db, "games", gameId), { [myTypingField]: false }).catch(() => {});
    setText("");
  };

  const makeMove = async (move) => {
    const isPlayerA = gameData.playerA === user.uid;
    const update = isPlayerA ? { playerAMove: move } : { playerBMove: move };

    await updateDoc(doc(db, "games", gameId), update);
  };

  // Stable across re-renders (the timer interval re-renders Chat every 100ms,
  // which would otherwise hand MatchIntro a brand-new onDone each tick and
  // reset its stage-advance timeouts before they ever fire).
  const handleIntroDone = useCallback(() => setShowIntro(false), []);

  // Both players get a fact at once — one about player A, one about player
  // Both players always learn the same *category* of information about each
  // other (e.g. both get "last move" or both get "total games played") so
  // the intel reveal is fair and symmetrical. Strategy:
  //   1. Build fact pools for both subjects independently (async, cached locally).
  //   2. Find the intersection of available fact *types*.
  //   3. Pick one shared type deterministically (no slot in the hash seed so
  //      both clients arrive at the same type).
  //   4. Write each subject's fact of that type. If there's no shared type
  //      at all (e.g. one player is brand new), fall back to independent picks.
  const postReveal = async (stageId) => {
    const uidA = gameData.playerA;
    const uidB = gameData.playerB;
    const profileA = uidA === user.uid ? userProfile : opponentProfile;
    const profileB = uidB === user.uid ? userProfile : opponentProfile;

    const [factsA, factsB] = await Promise.all([
      buildFactCandidates(profileA, uidA),
      buildFactCandidates(profileB, uidB),
    ]);

    const typesA = new Set(factsA.map((f) => f.type));
    const typesB = new Set(factsB.map((f) => f.type));
    const sharedTypes = [...typesA].filter((t) => typesB.has(t));

    const seed = hashString(gameId + stageId);
    let factA, factB;

    if (sharedTypes.length > 0) {
      const chosenType = sharedTypes[seed % sharedTypes.length];
      factA = factsA.find((f) => f.type === chosenType) ?? null;
      factB = factsB.find((f) => f.type === chosenType) ?? null;
    } else {
      factA = factsA.length > 0 ? factsA[hashString(gameId + stageId + "a") % factsA.length] : null;
      factB = factsB.length > 0 ? factsB[hashString(gameId + stageId + "b") % factsB.length] : null;
    }

    await Promise.all([
      factA ? postRevealSlot(stageId, "a", uidA, factA) : Promise.resolve(),
      factB ? postRevealSlot(stageId, "b", uidB, factB) : Promise.resolve(),
    ]);
  };

  const postRevealSlot = async (stageId, slot, subjectUid, fact) => {
    try {
      await setDoc(doc(db, "games", gameId, "messages", `${stageId}-${slot}`), {
        fact,
        system: true,
        subjectUid,
        senderUid: null,
        createdAt: serverTimestamp(),
        clientCreatedAt: Date.now(),
      });
    } catch (error) {
      // Expected when the other client already created this doc first —
      // the messages rule has no "update" branch, so this is a clean no-op.
    }
  };

  const leaveGame = async () => {
    if (!window.confirm("leave this game? this forfeits the match for both players.")) return;
    await deleteDoc(doc(db, "games", gameId));
  };

  // "rounded" (the default) keeps the directional speech-bubble tail;
  // "pill"/"sharp" are symmetric so the same radius works on either side.
  const bubbleRadius = (radius, isMe) => {
    if (radius === "pill") return BUBBLE_RADIUS.pill;
    if (radius === "sharp") return BUBBLE_RADIUS.sharp;
    return isMe ? "16px 16px 4px 16px" : "16px 16px 16px 4px";
  };

  const formatTime = (ms) => {
    if (ms <= DECISION_WINDOW) {
      const seconds = Math.floor(ms / 1000);
      const deciseconds = Math.floor((ms % 1000) / 100);
      return `${seconds}.${deciseconds}s`;
    }
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds.toString().padStart(2, '0')}s` : `${seconds}s`;
  };

  // What's coming up next — the next intel reveal, or voting once both
  // reveals are done — and how long until it, so the phase indicator can
  // say e.g. "1m 30s until intel" instead of a vague "chat phase".
  const upcomingMilestones = [
    ...REVEAL_STAGES.map((stage, i) => ({ at: stage.thresholdMs, label: i === 0 ? "until intel" : "until more intel" })),
    { at: DECISION_WINDOW, label: "until voting" },
  ];
  const nextMilestone = upcomingMilestones
    .filter((m) => m.at < timeRemaining)
    .sort((a, b) => b.at - a.at)[0];

  if (!gameData || !opponentProfile) return <p style={{ textAlign: "center", marginTop: "40px", color: TEXT_MUTED }}>loading game...</p>;

  const isPlayerA = gameData.playerA === user.uid;
  const myMove = isPlayerA ? gameData.playerAMove : gameData.playerBMove;
  const opponentMoved = isPlayerA ? !!gameData.playerBMove : !!gameData.playerAMove;
  const opponentTyping = isPlayerA ? gameData.typingB : gameData.typingA;
  const myAnswer = isPlayerA ? gameData.answerA : gameData.answerB;
  const opponentAnswer = isPlayerA ? gameData.answerB : gameData.answerA;
  // Fallback only matters for games created before "rules" was stamped on the doc.
  const rules = gameData.rules || { cc: 3, dd: 1, cd_defector: 5, cd_cooperator: 0 };
  const decisionTime = timeRemaining <= DECISION_WINDOW;
  const timerColor = decisionTime ? BETRAY_COLOR : timeRemaining <= 60000 ? "#d4a23a" : TRUST_COLOR;
  const timerBg = decisionTime ? BETRAY_BG : timeRemaining <= 60000 ? "#fdf6e8" : TRUST_BG;
  // A bought "bubble" cosmetic re-skins only the buyer's own sent bubbles —
  // everyone still sees it on both sides, same as the trust/betray default.
  const myBubble = getEquippedItem(userProfile, shopItems, "bubble");
  const opponentBubble = getEquippedItem(opponentProfile, shopItems, "bubble");

  // The two intel reveals for a stage ("reveal-2min-a"/"-b") are written at
  // the same moment, so they land adjacently in the time-ordered feed —
  // merge them into a single card instead of two separate stacked bubbles.
  const groupedMessages = [];
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m.system) {
      const stageKey = m.id.replace(/-[ab]$/, "");
      const group = [m];
      while (i + 1 < messages.length && messages[i + 1].system && messages[i + 1].id.replace(/-[ab]$/, "") === stageKey) {
        group.push(messages[++i]);
      }
      groupedMessages.push({ kind: "reveal", id: stageKey, items: group });
    } else {
      groupedMessages.push({ kind: "message", id: m.id, data: m });
    }
  }

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: GRADIENT_BG }}>
      {showIntro && <MatchIntro userProfile={userProfile} opponentProfile={opponentProfile} shopItems={shopItems} onDone={handleIntroDone} />}
      <div style={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column", maxWidth: "1280px", margin: "0 auto", background: "#fff", boxShadow: "0 0 50px rgba(0,0,0,0.12)" }}>
        {/* Header */}
        <div style={{ borderBottom: `1px solid ${BORDER}`, padding: "14px 20px 16px", background: "#fff" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <Logo size={22} />
            <button
              onClick={leaveGame}
              style={{ padding: "2px 10px", fontSize: "0.6875rem", background: "none", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "4px" }}
            >
              leave game
            </button>
          </div>
          {/* Grid (not flex space-between) so the timer stays dead-center */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", marginBottom: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <AvatarRing photoURL={userProfile.photoURL} size={48} item={getEquippedItem(userProfile, shopItems, "border")} fallbackBorder={`2px solid ${TRUST_COLOR}`} />
              <div>
                <div style={{ fontWeight: "600", fontSize: "0.8125rem" }}>you</div>
                <SubtitleBadge item={getEquippedItem(userProfile, shopItems, "subtitle")} />
              </div>
            </div>

            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: decisionTime ? "5px 10px" : "4px 8px",
              borderRadius: "20px",
              background: timerBg,
              border: `1.5px solid ${timerColor}`,
              animation: timeRemaining <= 10000 ? "pulse 1s infinite" : "none",
              justifySelf: "center",
            }}>
              <Clock size={12} color={timerColor} />
              <span style={{
                fontSize: decisionTime ? "0.9375rem" : "0.8125rem",
                fontWeight: "700",
                color: timerColor,
                fontFamily: "monospace",
              }}>
                {formatTime(timeRemaining)}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px" }}>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: "600", fontSize: "0.8125rem" }}>opponent</div>
                <SubtitleBadge item={getEquippedItem(opponentProfile, shopItems, "subtitle")} />
              </div>
              <AvatarRing photoURL={opponentProfile.photoURL} size={48} item={getEquippedItem(opponentProfile, shopItems, "border")} fallbackBorder={`2px solid ${BETRAY_COLOR}`} />
            </div>
          </div>

          {/* Time progress bar */}
          <div style={{ height: "4px", borderRadius: "2px", background: "#eef0f5", overflow: "hidden", marginBottom: "12px" }}>
            <div style={{
              height: "100%",
              width: `${((GAME_DURATION - timeRemaining) / GAME_DURATION) * 100}%`,
              background: timerColor,
              borderRadius: "2px",
              transition: "width 0.2s linear",
            }} />
          </div>

          {/* Phase Indicator */}
          <div style={{
            padding: "8px 12px",
            borderRadius: "8px",
            background: decisionTime ? BETRAY_BG : "#f5f5f7",
            border: `1px solid ${decisionTime ? BETRAY_COLOR : BORDER}`,
            fontSize: "0.75rem",
            fontWeight: "600",
            textAlign: "center",
            marginBottom: "12px",
            color: decisionTime ? BETRAY_COLOR : TEXT_MUTED
          }}>
            {decisionTime
              ? "decision time"
              : nextMilestone
                ? `${formatTime(timeRemaining - nextMilestone.at)} ${nextMilestone.label}`
                : "decision time"}
          </div>

          {/* Compact Matrix — collapsed by default so it doesn't compete
              with the chat itself; the matrix doesn't change mid-game, so
              there's no need to show it until someone wants a reminder. */}
          <button
            onClick={() => setShowRules((s) => !s)}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: "5px",
              width: "100%", padding: "7px", background: "none", border: `1px solid ${BORDER}`,
              borderRadius: "8px", fontSize: "0.6875rem", fontWeight: "600", color: TEXT_MUTED,
              marginBottom: showRules ? "8px" : 0,
            }}
          >
            today's rules
            {showRules ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          {showRules && <PayoffMatrixGrid compact={true} rules={rules} />}
        </div>

        {/* Chat Messages */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px",
          background: "#fbfbfc",
          opacity: decisionTime && !myMove ? 0.5 : 1
        }}>
          {/* Today's question lives at the very start of the feed, like the
              first thing said in the conversation, rather than as a static
              header bar above the chat. */}
          {gameData.question && myAnswer && (
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "18px" }}>
              <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "14px", padding: "14px 18px", maxWidth: "320px", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", marginBottom: "8px" }}>
                  <HelpCircle size={14} color={TEXT_MUTED} />
                  <span style={{ fontSize: "0.625rem", fontWeight: "700", color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    today's question
                  </span>
                </div>
                <div style={{ fontSize: "0.8125rem", fontWeight: "600", marginBottom: "12px" }}>{gameData.question.text}</div>
                <div style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                  <AnswerChip label="you" value={myAnswer} photoURL={userProfile.photoURL} highlight={!!opponentAnswer && myAnswer === opponentAnswer} />
                  <AnswerChip label="them" value={opponentAnswer} photoURL={opponentProfile.photoURL} highlight={!!opponentAnswer && myAnswer === opponentAnswer} />
                </div>
                {opponentAnswer && (
                  <div style={{ marginTop: "10px", fontSize: "0.6875rem", fontWeight: "700", color: myAnswer === opponentAnswer ? TRUST_COLOR : MIXED_COLOR }}>
                    {myAnswer === opponentAnswer ? "you both agree!" : "different answers"}
                  </div>
                )}
              </div>
            </div>
          )}

          {messages.length === 0 && !opponentTyping && (
            <div style={{ textAlign: "center", padding: "40px 20px", color: TEXT_MUTED, fontSize: "0.8125rem" }}>
              no messages yet. start the conversation...
            </div>
          )}

          {groupedMessages.map((entry) => {
            if (entry.kind === "reveal") {
              // What you learned about THEM is the useful, actionable part —
              // shown first and bold. What they learned about YOU is just
              // FYI — shown second and faded, and phrased as "you" rather
              // than the confusing third-person "they" (since it's you).
              const sortedItems = [...entry.items].sort((a, b) => {
                const aIsMe = a.subjectUid === user.uid ? 1 : 0;
                const bIsMe = b.subjectUid === user.uid ? 1 : 0;
                return aIsMe - bIsMe;
              });
              return (
                <div key={entry.id} style={{ display: "flex", justifyContent: "center", marginBottom: "16px", animation: "slideIn 0.2s ease" }}>
                  <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "14px", padding: "12px 16px", width: "88%", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                    <div style={{ fontSize: "0.625rem", fontWeight: "700", color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.03em", textAlign: "center", marginBottom: "10px" }}>
                      intel
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {sortedItems.map((m, i) => {
                        const isMe = m.subjectUid === user.uid;
                        const subjectProfile = isMe ? userProfile : opponentProfile;
                        const ringColor = isMe ? TRUST_COLOR : BETRAY_COLOR;
                        return (
                          <div
                            key={m.id}
                            style={{
                              display: "flex", alignItems: "center", gap: "10px",
                              paddingTop: i > 0 ? "10px" : 0,
                              borderTop: i > 0 ? `1px solid ${BORDER}` : "none",
                              opacity: isMe ? 0.55 : 1,
                            }}
                          >
                            <AvatarRing photoURL={subjectProfile.photoURL} size={24} item={getEquippedItem(subjectProfile, shopItems, "border")} fallbackBorder={`2px solid ${ringColor}`} />
                            <div>
                              <div style={{ fontSize: "0.5625rem", fontWeight: "700", color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: "1px" }}>
                                {isMe ? "you" : "opponent"}
                              </div>
                              <div style={{ fontSize: "0.8125rem", color: TEXT_DARK, fontWeight: isMe ? "500" : "700" }}>{renderFact(m.fact, isMe)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            const m = entry.data;
            const isMe = m.senderUid === user.uid;
            return (
              <div
                key={m.id}
                style={{
                  display: "flex",
                  justifyContent: isMe ? "flex-end" : "flex-start",
                  marginBottom: "14px",
                  animation: "slideIn 0.2s ease",
                  alignItems: "flex-end"
                }}
              >
                {!isMe && (
                  <div style={{ marginRight: "8px", flexShrink: 0 }}>
                    <AvatarRing photoURL={opponentProfile.photoURL} size={32} item={getEquippedItem(opponentProfile, shopItems, "border")} />
                  </div>
                )}

                <div style={{
                  maxWidth: "min(70%, 480px)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isMe ? "flex-end" : "flex-start"
                }}>
                  <div style={{
                    background: isMe ? (myBubble?.config.background || TRUST_COLOR) : (opponentBubble?.config.background || BETRAY_COLOR),
                    color: isMe ? (myBubble?.config.color || "#fff") : (opponentBubble?.config.color || "#fff"),
                    padding: "10px 14px",
                    borderRadius: bubbleRadius(isMe ? myBubble?.config.radius : opponentBubble?.config.radius, isMe),
                    fontWeight: (isMe ? myBubble?.config.bold : opponentBubble?.config.bold) ? "800" : "400",
                    fontSize: "0.875rem",
                    lineHeight: "1.4",
                    wordBreak: "break-word",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                  }}>
                    {filterProfanity(m.text)}
                  </div>
                </div>

                {isMe && (
                  <div style={{ marginLeft: "8px", flexShrink: 0 }}>
                    <AvatarRing photoURL={userProfile.photoURL} size={32} item={getEquippedItem(userProfile, shopItems, "border")} />
                  </div>
                )}
              </div>
            );
          })}

          {opponentTyping && (
            <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-end" }}>
              <div style={{ marginRight: "8px", flexShrink: 0 }}>
                <AvatarRing photoURL={opponentProfile.photoURL} size={32} item={getEquippedItem(opponentProfile, shopItems, "border")} />
              </div>
              <div style={{ background: "#fff", padding: "12px 18px", borderRadius: "16px 16px 16px 4px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                <div className="typing-dots"><span></span><span></span><span></span></div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Decision Buttons — only appear once decision time actually starts */}
        {decisionTime && (
          myMove ? (
            <div style={{ padding: "18px", background: myMove === "cooperate" ? TRUST_BG : BETRAY_BG, borderTop: `1px solid ${myMove === "cooperate" ? TRUST_COLOR : BETRAY_COLOR}` }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontSize: "0.875rem", fontWeight: "600", marginBottom: "6px" }}>
                  you chose <MoveIcon move={myMove} size={20} />
                </div>
                <div style={{ fontSize: "0.75rem", color: TEXT_MUTED }}>
                  {!opponentMoved ? "waiting for them..." : "both decided"}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: "16px 20px", background: "#fff", borderTop: `1px solid ${BORDER}` }}>
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => makeMove("cooperate")}
                  style={{
                    flex: 1,
                    padding: "14px",
                    background: TRUST_COLOR,
                    color: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    fontSize: "0.875rem",
                    fontWeight: "700",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px"
                  }}
                >
                  <MoveIcon move="cooperate" size={20} color="#fff" />
                  trust
                </button>
                <button
                  onClick={() => makeMove("defect")}
                  style={{
                    flex: 1,
                    padding: "14px",
                    background: BETRAY_COLOR,
                    color: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    fontSize: "0.875rem",
                    fontWeight: "700",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px"
                  }}
                >
                  <MoveIcon move="defect" size={20} color="#fff" />
                  betray
                </button>
              </div>
            </div>
          )
        )}

        {/* Chat Input */}
        {!decisionTime && !myMove && (
          <div style={{ padding: "12px 20px", background: "#fff", borderTop: `1px solid ${BORDER}` }}>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                value={text}
                onChange={handleTextChange}
                onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                placeholder="type a message..."
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  border: `1px solid ${BORDER}`,
                  borderRadius: "24px",
                  fontSize: "0.875rem",
                  outline: "none"
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!text.trim()}
                style={{
                  padding: "12px 24px",
                  background: text.trim() ? TRUST_COLOR : "#eceef3",
                  color: "#fff",
                  border: "none",
                  borderRadius: "24px",
                  fontWeight: "600",
                  fontSize: "0.875rem"
                }}
              >
                send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MatchIntro({ userProfile, opponentProfile, shopItems, onDone }) {
  const [stage, setStage] = useState("you"); // you -> them -> vs

  useEffect(() => {
    const timeouts = [
      setTimeout(() => setStage("them"), 700),
      setTimeout(() => setStage("vs"), 1500),
      setTimeout(() => onDone(), 2700),
    ];
    return () => timeouts.forEach(clearTimeout);
  }, [onDone]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 50,
      background: GRADIENT_BG,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{ marginBottom: "36px" }}>
        <Logo size={26} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "28px" }}>
        <IntroAvatar
          photoURL={userProfile.photoURL}
          label="you"
          color={TRUST_COLOR}
          show={true}
          borderItem={getEquippedItem(userProfile, shopItems, "border")}
          subtitleItem={getEquippedItem(userProfile, shopItems, "subtitle")}
        />
        <div style={{
          fontSize: "0.8125rem", fontWeight: "700", color: TEXT_MUTED,
          opacity: stage === "them" || stage === "vs" ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}>
          vs
        </div>
        <IntroAvatar
          photoURL={opponentProfile.photoURL}
          label="opponent"
          color={BETRAY_COLOR}
          show={stage === "them" || stage === "vs"}
          borderItem={getEquippedItem(opponentProfile, shopItems, "border")}
          subtitleItem={getEquippedItem(opponentProfile, shopItems, "subtitle")}
        />
      </div>
      {stage === "vs" && (
        <div style={{ marginTop: "30px", fontSize: "0.8125rem", color: TEXT_MUTED, animation: "fadeIn 0.3s ease" }}>
          match starting...
        </div>
      )}
    </div>
  );
}

function IntroAvatar({ photoURL, label, color, show, borderItem, subtitleItem }) {
  return (
    <div style={{
      textAlign: "center",
      opacity: show ? 1 : 0,
      transform: show ? "scale(1)" : "scale(0.6)",
      transition: "all 0.4s ease",
    }}>
      <AvatarRing photoURL={photoURL} size={76} item={borderItem} fallbackBorder={`3px solid ${color}`} />
      <div style={{ marginTop: "8px", fontSize: "0.75rem", fontWeight: "600", color: TEXT_MUTED }}>{label}</div>
      <SubtitleBadge item={subtitleItem} />
    </div>
  );
}

function AnswerChip({ label, value, photoURL, highlight }) {
  return (
    <div style={{ textAlign: "center" }}>
      <img src={photoURL} alt="" style={{ width: "22px", height: "22px", borderRadius: "50%", marginBottom: "4px", objectFit: "cover", objectPosition: "center" }} />
      <div style={{ fontSize: "0.625rem", color: TEXT_MUTED }}>{label}</div>
      <div style={{ fontSize: "0.75rem", fontWeight: "700", color: highlight ? TRUST_COLOR : TEXT_DARK }}>{value || "…"}</div>
    </div>
  );
}

export default Chat;
